import {
    Input,
    Autocomplete,
    AutocompleteItem,
    DateRangePicker,
    Select,
    SelectItem,
} from "@heroui/react";
import { FilterCard } from "@/components/data-table";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";

/**
 * Filter component for Stock Transactions (History) page
 * Backend: stock-history/data
 * Filters: warehouse_id, search, date_from, date_to, activity_source, type
 *
 * @param {Object} filterOptions - Options for select filters (passed from parent)
 *   - warehouses: Array of { value, title }
 */
const FilterData = ({ table, initialFilters, filterOptions = {} }) => {
    const { filterPanel } = table;
    const { isOpen, pending, setPending, apply, clear } = filterPanel;

    const { warehouses = [] } = filterOptions;

    // Handle clear
    const handleClear = () => {
        setPending(initialFilters);
        clear();
    };

    // Derive dateRange from pending
    const dateRange =
        pending.date_from && pending.date_to
            ? {
                  start: parseDate(pending.date_from),
                  end: parseDate(pending.date_to),
              }
            : null;

    // Handle date range change
    const handleDateRangeChange = (range) => {
        if (range) {
            setPending((prev) => ({
                ...prev,
                date_from: range.start.toString(),
                date_to: range.end.toString(),
            }));
        } else {
            setPending((prev) => ({
                ...prev,
                date_from: "",
                date_to: "",
            }));
        }
    };

    return (
        <FilterCard isVisible={isOpen} onClear={handleClear} onApply={apply}>
            {/* Pencarian Material */}
            <Input
                label="Cari Material"
                placeholder="Nama material..."
                value={pending.search || ""}
                onValueChange={(val) =>
                    setPending((prev) => ({ ...prev, search: val }))
                }
                variant="flat"
                isClearable
            />

            {/* Gudang */}
            <Autocomplete
                label="Gudang"
                placeholder="Semua gudang"
                selectedKey={
                    pending.warehouse_id ? String(pending.warehouse_id) : null
                }
                onSelectionChange={(key) =>
                    setPending((prev) => ({
                        ...prev,
                        warehouse_id: key || "",
                    }))
                }
                variant="flat"
                isClearable
            >
                {warehouses.map((w) => (
                    <AutocompleteItem key={String(w.value)} value={w.value}>
                        {w.title}
                    </AutocompleteItem>
                ))}
            </Autocomplete>

            {/* Rentang Tanggal */}
            <DateRangePicker
                label="Rentang Tanggal"
                value={dateRange}
                onChange={handleDateRangeChange}
                maxValue={today(getLocalTimeZone())}
                variant="flat"
            />

            {/* Tipe Transaksi */}
            <Select
                label="Tipe"
                placeholder="Semua tipe"
                selectedKeys={
                    pending.type ? new Set([pending.type]) : new Set()
                }
                onSelectionChange={(keys) =>
                    setPending((prev) => ({
                        ...prev,
                        type: Array.from(keys)[0] || "",
                    }))
                }
                variant="flat"
            >
                <SelectItem key="in">Masuk</SelectItem>
                <SelectItem key="out">Keluar</SelectItem>
            </Select>

            {/* Sumber Aktivitas */}
            <Select
                label="Sumber Aktivitas"
                placeholder="Semua sumber"
                selectionMode="multiple"
                selectedKeys={
                    pending.activity_source
                        ? new Set(pending.activity_source.split(","))
                        : new Set()
                }
                onSelectionChange={(keys) =>
                    setPending((prev) => ({
                        ...prev,
                        activity_source: Array.from(keys).join(","),
                    }))
                }
                variant="flat"
            >
                <SelectItem key="initial_stock">Stok Awal</SelectItem>
                <SelectItem key="stock_out">Stok Keluar</SelectItem>
                <SelectItem key="purchase">Pembelian</SelectItem>
                <SelectItem key="sales">Penjualan</SelectItem>
                <SelectItem key="adjustment">Penyesuaian</SelectItem>
                <SelectItem key="transfer">Transfer</SelectItem>
            </Select>
        </FilterCard>
    );
};

export default FilterData;
